import { Language, Subsidiary } from '@coolblue-development/constants';
import { ArticleContentType } from '@features/article/types';

import {
  ADDITIONAL_AD_SCALE_AMOUNT,
  ARTICLE_CONTENT_TYPE_GROUPS,
  BRAND_PAGE,
  BRAND_PRODUCT_TYPE_PAGE,
  CLOSED_COMMUNITY_PAGE,
  COOLBLUE_GPT_NETWORK_CODE,
  GENERAL_PRODUCT_TYPE_PAGE
} from './CoolblueAds.constants';
import { GetPageKeyValueTargetingProps, KeyValueTargetingProps } from './CoolblueAds.types';

// Calculates the scale factor to resize the ad to fit the container.
function resizeAd(adWidth: number, adHeight: number, slotId: string, isFilter = false) {
  const element = document.querySelector(`#${slotId}`);

  if (!(element instanceof HTMLElement)) return;

  const getAdStyling = (isFilter: boolean) => ({
    zIndex: 2,
    transformOrigin: isFilter ? 'top left' : 'center left'
  });

  const containerWidth = element.offsetWidth;
  const containerHeight = element.offsetHeight;

  const getScale = (containerSize: number, adSize: number) =>
    containerSize / adSize + ADDITIONAL_AD_SCALE_AMOUNT;

  const scaleHeight = getScale(containerHeight, adHeight);
  const scaleWidth = getScale(containerWidth, adWidth);

  if (scaleHeight > scaleWidth) {
    return {
      ...getAdStyling(isFilter),
      transform: `scale(${scaleHeight})`
    };
  }

  return {
    ...getAdStyling(isFilter),
    transform: `scale(${scaleWidth})`
  };
}

// Creates a unique ID for the ad slot based on the path and type (mobile or desktop).
function generateIdForSlotName(adUnitPath: string, type: 'mobile' | 'desktop') {
  return `cb-gpt${adUnitPath.toLowerCase().replace(/[^a-z0-9-]/g, '-') + '-' + type}`;
}

function getLanguageSuffixForAdUnitPath(language: Language, subsidiary: Subsidiary) {
  // If nl-NL don't add the suffix (because of how the google ad manager is set up).
  if (language === 'nl' && subsidiary === 'nl') {
    return '';
  }

  return language;
}

function createAdUnitPathBySlotName(slotName: string, subsidiary: Subsidiary, language: Language) {
  const domain = `coolblue.${subsidiary}`;
  let languageSuffix = getLanguageSuffixForAdUnitPath(language, subsidiary);
  languageSuffix = languageSuffix && `-${languageSuffix}`;

  return `/${COOLBLUE_GPT_NETWORK_CODE}/${domain}/${domain}${languageSuffix}_${slotName}`;
}

function createAdUnitPathByPageBlockType(
  blockType: string,
  blockTypeIndex: number,
  path: string,
  subsidiary: Subsidiary,
  language: Language
) {
  const strippedPath = path.replace(/^\//, '');
  const domain = `coolblue.${subsidiary}`;
  const adIndex = blockTypeIndex + 1; // 1-based index for ad unit names

  let languageSuffix = getLanguageSuffixForAdUnitPath(language, subsidiary);
  languageSuffix = languageSuffix && `_${languageSuffix}`;

  return `/${COOLBLUE_GPT_NETWORK_CODE}/${domain}/${domain}${languageSuffix}_${strippedPath}_${blockType}_${adIndex}`;
}

function getPageKeyValueTargeting({
  language,
  urlPath,
  pageTitle,
  contentType,
  productTypeIds,
  brandIds,
  isClosedCommunity = false
}: GetPageKeyValueTargetingProps): KeyValueTargetingProps {
  const pageType = getPageType(contentType, isClosedCommunity);

  return {
    position: '',
    language,
    urlPath,
    pageTitle,
    pageType,
    productTypeID: productTypeIds,
    brandID: brandIds
  };
}

function getPageType(
  contentType: ArticleContentType,
  isClosedCommunity: boolean,
  productTypeIds?: string[],
  brandIds?: string[]
) {
  if (isClosedCommunity) {
    return CLOSED_COMMUNITY_PAGE;
  }

  if (contentType) {
    if (ARTICLE_CONTENT_TYPE_GROUPS.generalProductContentTypes.includes(contentType)) {
      return GENERAL_PRODUCT_TYPE_PAGE;
    }

    if (ARTICLE_CONTENT_TYPE_GROUPS.brandProductContentTypes.includes(contentType)) {
      return BRAND_PRODUCT_TYPE_PAGE;
    }

    if (ARTICLE_CONTENT_TYPE_GROUPS.brandContentTypes.includes(contentType)) {
      return BRAND_PAGE;
    }
  }

  if (productTypeIds && productTypeIds.length > 0) {
    if (brandIds && brandIds.length === 1) {
      return BRAND_PRODUCT_TYPE_PAGE;
    }

    return GENERAL_PRODUCT_TYPE_PAGE;
  }

  if (brandIds && brandIds.length === 1) {
    return BRAND_PAGE;
  }

  return '';
}

export {
  createAdUnitPathByPageBlockType,
  createAdUnitPathBySlotName,
  generateIdForSlotName,
  getLanguageSuffixForAdUnitPath,
  getPageKeyValueTargeting,
  getPageType,
  resizeAd
};
